import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './ThemeContext';
import {
  DEFAULT_CUSTOM_PALETTE,
  paletteToCssVars,
  type CustomPalette,
} from './themes';

/**
 * Modal dialog that lets the user pick the four anchor colors for a custom
 * theme: background, surface, text, and accent. The remaining CSS tokens
 * (muted text, hover accent, borders, gradient) are derived in `themes.ts`.
 *
 * - Save persists the palette via the ThemeContext (localStorage + best-effort
 *   server sync) and switches the active theme to `'custom'`.
 * - Cancel discards in-flight edits.
 * - The modal does NOT itself reopen the theme picker on close; the caller
 *   decides via the `onClose` callback whether to reopen.
 */
interface Props {
  open: boolean;
  onClose(): void;
}

const FIELDS: Array<{ key: keyof CustomPalette }> = [
  { key: 'bg' },
  { key: 'surface' },
  { key: 'text' },
  { key: 'accent' },
];

export function CustomThemeModal({ open, onClose }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const { customPalette, saveCustomPalette } = useTheme();
  const [palette, setPalette] = useState<CustomPalette>(
    customPalette ?? DEFAULT_CUSTOM_PALETTE,
  );

  // Reset edits whenever the modal reopens so a previous "Cancel" doesn't
  // persist staged-but-thrown-away colors into the next open.
  useEffect(() => {
    if (open) setPalette(customPalette ?? DEFAULT_CUSTOM_PALETTE);
  }, [open, customPalette]);

  // Close on Escape, mirroring the picker's keyboard behavior.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Preview swatches use derived CSS vars so the user can see what muted text
  // and surfaces will look like without applying the change to the whole app.
  const previewVars = useMemo(() => paletteToCssVars(palette), [palette]);

  if (!open) return null;

  const setField = (key: keyof CustomPalette, value: string): void => {
    setPalette((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    saveCustomPalette(palette);
    onClose();
  };

  return (
    <div
      className="custom-theme-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('theme.editorLabel')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="custom-theme-modal" onSubmit={onSubmit}>
        <h2 className="custom-theme-modal__title">{t('theme.title')}</h2>
        <p className="custom-theme-modal__hint">
          {t('theme.pickColors')}
        </p>

        <div className="custom-theme-modal__grid">
          {FIELDS.map(({ key }) => {
            const label = t(`theme.fields.${key}`);
            return (
            <label key={key} className="custom-theme-modal__field">
              <span className="custom-theme-modal__field-label">{label}</span>
              <span className="custom-theme-modal__field-row">
                <input
                  type="color"
                  aria-label={t('theme.colorLabel', { label })}
                  value={palette[key]}
                  onChange={(e) => setField(key, e.target.value)}
                />
                <input
                  type="text"
                  aria-label={t('theme.hexLabel', { label })}
                  className="custom-theme-modal__hex"
                  value={palette[key]}
                  spellCheck={false}
                  onChange={(e) => setField(key, e.target.value)}
                />
              </span>
              <span className="custom-theme-modal__field-hint">{t(`theme.hints.${key}`)}</span>
            </label>
            );
          })}
        </div>

        {/* Preview rendered inside an isolated scope using the derived vars,
            so the live app stays on the previous theme until Save. */}
        <div
          className="custom-theme-modal__preview"
          aria-label={t('theme.previewLabel')}
          style={previewVars as React.CSSProperties}
        >
          <div className="custom-theme-modal__preview-card">
            <span className="custom-theme-modal__preview-title">{t('theme.preview')}</span>
            <span className="custom-theme-modal__preview-muted">{t('theme.previewMuted')}</span>
            <button type="button" className="custom-theme-modal__preview-btn" tabIndex={-1}>
              {t('theme.accentButton')}
            </button>
          </div>
        </div>

        <div className="custom-theme-modal__actions">
          <button type="button" className="btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn">{t('theme.saveTheme')}</button>
        </div>
      </form>
    </div>
  );
}
